import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Select from 'tio-ui/components/select';
import { gt, or } from 'tio-ui/utilities';
import { t } from 'ember-intl';
import StatePill from 'tio-employee/components/tas/state-pill';
import ActivityCard from 'tio-employee/components/tas/activity-card';
import BenefitHistorySummary from 'tio-common/components/tas/benefit-history-summary';
import {
  applicationAmountInfo as computeAmountInfo,
  getApplicationName,
  getInstanceName,
  getProgramName,
} from 'tio-employee/utils/tas/activity-helpers';
import type { IntlService } from 'ember-intl';
import type PartnerService from 'tio-employee/services/partner';
import { isInRepayment } from 'tio-common/utils/tuition-assistance/application-states';
import type {
  RecentApplication,
  ProgramInstance,
} from 'tio-common/utils/tuition-assistance/application-states';
import type { BenefitHistoryModel } from 'tio-employee/routes/authenticated/tas/benefit-history';

interface UnifiedHistoryItem {
  type: 'application' | 'instance';
  updatedAt: string;
  application?: RecentApplication;
  instance?: ProgramInstance;
}

interface BenefitHistorySignature {
  Args: {
    model: BenefitHistoryModel;
  };
}

export default class TasBenefitHistory extends Component<BenefitHistorySignature> {
  @service declare intl: IntlService;
  @service declare partner: PartnerService;

  @tracked selectedYear = 'all';

  get selectedYearValue() {
    return this.selectedYear || this.args.model.currentYear.toString();
  }

  get yearOptions() {
    const options: { key: string; label: string }[] = [
      { key: 'all', label: this.intl.t('tas.benefit_history.all_years') },
    ];

    // Add years in descending order
    for (const year of this.args.model.availableYears) {
      options.push({ key: year.toString(), label: `${year}-${year + 1}` });
    }

    return options;
  }

  get filteredApplications() {
    if (this.selectedYearValue === 'all') {
      return this.args.model.applications;
    }

    const yearFilter = parseInt(this.selectedYearValue, 10);
    const filtered = this.args.model.applications.filter((app) => {
      // Use updatedAt to match displayed "last action" date
      const dateStr = app.updatedAt || app.createdAt;
      if (!dateStr) {
        return false;
      }
      const year = new Date(dateStr).getFullYear();
      return year === yearFilter || year === yearFilter + 1;
    });
    return filtered;
  }

  get filteredProgramInstances() {
    if (this.selectedYearValue === 'all') {
      return this.args.model.programInstances;
    }

    const yearFilter = parseInt(this.selectedYearValue, 10);
    const filteredInstances = this.args.model.programInstances
      .map((instance) => {
        // Filter child applications by year using updatedAt (matches displayed date)
        const filteredChildren = instance.childApplications.filter((app) => {
          const dateStr = app.updatedAt || app.createdAt;
          if (!dateStr) return false;
          const year = new Date(dateStr).getFullYear();
          return year === yearFilter || year === yearFilter + 1;
        });

        const instanceDateStr =
          instance.instanceApplication.updatedAt || instance.instanceApplication.createdAt;
        const instanceMatchesYear = (() => {
          if (!instanceDateStr) return false;
          const year = new Date(instanceDateStr).getFullYear();
          return year === yearFilter || year === yearFilter + 1;
        })();

        // Return instance if it or its children match the year filter
        if (filteredChildren.length > 0 || instanceMatchesYear) {
          return {
            instanceApplication: instance.instanceApplication,
            childApplications: filteredChildren,
          };
        }

        return null;
      })
      .filter((instance): instance is NonNullable<typeof instance> => instance !== null);

    return filteredInstances;
  }

  get sortedHistoryItems() {
    const items: UnifiedHistoryItem[] = [];

    // Add flat applications
    for (const app of this.filteredApplications) {
      items.push({
        type: 'application',
        updatedAt: app.updatedAt || '',
        application: app,
      });
    }

    // Add Type 1 and Type 4.2/4.3 instances
    for (const instance of this.filteredProgramInstances) {
      items.push({
        type: 'instance',
        updatedAt: instance.instanceApplication.updatedAt || '',
        instance,
      });
    }

    // Sort by updated_at descending (most recent first)
    return items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }

  get hasHistory() {
    return this.sortedHistoryItems.length > 0;
  }

  get externalSpendLabel() {
    return (
      this.partner.settingModules?.externalSpendDescriptor ||
      this.intl.t('tas.dashboard.annual_benefits.external')
    );
  }

  /**
   * Compute amount info for a RecentApplication (OpenSearch fields, amounts in dollars).
   */
  applicationAmountInfo = (app: RecentApplication) => {
    return computeAmountInfo(app);
  };

  applicationPaymentState = (app: RecentApplication) =>
    isInRepayment(app) ? 'TAS.ApplicationPaymentState.REFUND_REQUESTED' : null;

  handleYearSelect = (key: string) => {
    this.selectedYear = key;
  };

  <template>
    <VStack>
      {{! Dependent/My Programs toggle — only shown when company has dependent programs }}
      {{#if @model.hasDependentPrograms}}
        <div class="tio-flex-extra-tight p-1 tio-surface-sunken self-end">
          <LinkTo
            @route="authenticated.tas.benefit-history"
            @query={{hash dependents=null}}
            class="px-4 py-1.5 rounded-md tio-text-sm-medium transition-colors
              {{if
                @model.dependentsOnly
                'tio-fg-secondary hover:tio-fg-primary'
                'tio-bg-surface shadow-sm tio-fg-brand'
              }}"
          >
            {{t "tas.benefit_history.my_programs"}}
          </LinkTo>
          <LinkTo
            @route="authenticated.tas.benefit-history"
            @query={{hash dependents="true"}}
            class="px-4 py-1.5 rounded-md tio-text-sm-medium transition-colors
              {{if
                @model.dependentsOnly
                'tio-bg-surface shadow-sm tio-fg-brand'
                'tio-fg-secondary hover:tio-fg-primary'
              }}"
          >
            {{t "common.tuition_assistance.dependent_programs"}}
          </LinkTo>
        </div>
      {{/if}}

      {{! Benefit History Summary — hidden in dependents-only view }}
      {{#unless @model.dependentsOnly}}
        <Section>
          <:header>
            <Header>{{t "tas.benefit_history.title"}}</Header>
          </:header>
          <:body>
            <BenefitHistorySummary
              @limitsStatusPrevious={{@model.limitsStatusPrevious}}
              @limitsStatusCurrent={{@model.limitsStatusCurrent}}
              @limitsStatusNext={{@model.limitsStatusNext}}
              @externalSpendLabel={{this.externalSpendLabel}}
            />
          </:body>
        </Section>
      {{/unless}}

      {{! Activity Section }}
      <Section>
        <:header>
          <div class="tio-row-between">
            <Header>{{#if @model.dependentsOnly}}{{t
                  "common.tuition_assistance.dependent_programs"
                }}{{else}}{{t "tas.benefit_history.activity_title"}}{{/if}}</Header>
            <Select
              @items={{this.yearOptions}}
              @selected={{this.selectedYearValue}}
              @onChange={{this.handleYearSelect}}
              @clearable={{false}}
              @size="sm"
              @label={{t "tas.benefit_history.filter_by_year"}}
              data-test-benefit-history-year-options
            />
          </div>
        </:header>
        <:body>
          {{#if this.hasHistory}}
            <div class="grid grid-cols-1 gap-4">
              {{#each this.sortedHistoryItems as |item|}}
                {{#if (eq item.type "instance")}}
                  {{#let item.instance as |instance|}}
                    {{#if instance}}
                      {{#if (gt instance.childApplications.length 0)}}
                        {{! Type 4 instance WITH children — bordered group container }}
                        <div
                          class="rounded-2xl border tio-border-default tio-bg-surface shadow-sm overflow-hidden"
                        >
                          {{! Program header row }}
                          <div
                            class="tio-row-between px-4 py-3 tio-bg-sunken border-b tio-border-default"
                          >
                            <div class="tio-row-tight">
                              <div class="tio-col-flush">
                                <Heading @level={{3}} @size="sm">
                                  {{getInstanceName instance.instanceApplication}}
                                </Heading>
                                <Text @variant="muted" @size="xs">
                                  {{getProgramName instance.instanceApplication}}
                                </Text>
                              </div>
                              <StatePill
                                @state={{instance.instanceApplication.tasProgramInstanceStateKeyword}}
                                @size="sm"
                              />
                            </div>
                            <LinkTo
                              @route="authenticated.tas.programs.show"
                              @model={{or
                                instance.instanceApplication.tasProgramInstanceIdKeyword
                                instance.instanceApplication.id
                              }}
                              class="tio-copy tio-text-sm-semibold"
                            >
                              {{t "tas.benefit_history.view_program"}}
                            </LinkTo>
                          </div>
                          {{! Child application cards }}
                          <div class="divide-y tio-divide-subtle">
                            {{#each instance.childApplications as |childApp|}}
                              <ActivityCard
                                @route="authenticated.tas.applications.show"
                                @model={{or childApp.tasApplicationIdKeyword childApp.id}}
                                @name={{getApplicationName childApp}}
                                @state={{childApp.tasApplicationStateKeyword}}
                                @shortId={{or childApp.tasApplicationIdKeyword childApp.id}}
                                @updatedAt={{or childApp.updatedAt childApp.createdAt}}
                                @amountInfo={{this.applicationAmountInfo childApp}}
                                @paymentState={{this.applicationPaymentState childApp}}
                                @variant="nested"
                              />
                            {{/each}}
                          </div>
                        </div>
                      {{else}}
                        {{! Program instance with NO children — standalone graduate card }}
                        <ActivityCard
                          @route="authenticated.tas.programs.show"
                          @model={{or
                            instance.instanceApplication.tasProgramInstanceIdKeyword
                            instance.instanceApplication.id
                          }}
                          @name={{getInstanceName instance.instanceApplication}}
                          @state={{instance.instanceApplication.tasProgramInstanceStateKeyword}}
                          @shortId={{or
                            instance.instanceApplication.tasProgramInstanceIdKeyword
                            instance.instanceApplication.id
                          }}
                          @programName={{getProgramName instance.instanceApplication}}
                          @updatedAt={{or
                            instance.instanceApplication.updatedAt
                            instance.instanceApplication.createdAt
                          }}
                          @institution={{instance.instanceApplication.tasProgramInstanceFieldSchoolInstitutionNameKeyword}}
                          @iconKind="program"
                        />
                      {{/if}}
                    {{/if}}
                  {{/let}}
                {{else}}
                  {{! Flat application (Type 2/3) — application card }}
                  {{#let item.application as |application|}}
                    {{#if application}}
                      <ActivityCard
                        @route="authenticated.tas.applications.show"
                        @model={{or application.tasApplicationIdKeyword application.id}}
                        @name={{getApplicationName application}}
                        @state={{application.tasApplicationStateKeyword}}
                        @shortId={{or application.tasApplicationIdKeyword application.id}}
                        @programName={{getProgramName application}}
                        @updatedAt={{or application.updatedAt application.createdAt}}
                        @amountInfo={{this.applicationAmountInfo application}}
                        @paymentState={{this.applicationPaymentState application}}
                      />
                    {{/if}}
                  {{/let}}
                {{/if}}
              {{/each}}
            </div>
          {{else}}
            <div class="text-center py-12 tio-fg-muted">
              {{#if (eq this.selectedYearValue "all")}}
                {{t "tas.benefit_history.no_activity"}}
              {{else}}
                {{t "tas.benefit_history.no_activity_for_year" year=this.selectedYearValue}}
              {{/if}}
            </div>
          {{/if}}
        </:body>
      </Section>
    </VStack>
  </template>
}
